import React, { useCallback } from 'react';
import { Box, Container, Fab, LinearProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router';
import { Sidebar } from './Sidebar';
import { AppBar } from './AppBar';
import { BottomNavigation } from './BottomNavigation';
import PdfStatusNotification from '../PdfStatusNotification/PdfStatusNotification';
import { MenuItem, User } from '../../types';
import { useSidebar } from '../../hooks/useSidebar';
import { usePdfStatus } from '../../hooks/usePdfStatus';
import { BOUNDARY_ROLES } from '../../constants/roles';
import { SUBMIT_SAMPLE_PATH } from '../../utils/navigation';
import { useBillingIa } from '../../hooks/useBillingIa';

/** Routes where the mobile "Submit a sample" FAB would sit on top of a submission form's own CTA. */
const FAB_HIDDEN_PATHS = ['/portal/analyse', '/portal/intake'];

interface LayoutProps {
  children: React.ReactNode;
  menuItems: MenuItem[];
  selectedMenu: string;
  onMenuSelect: (menuId: string) => void;
  onLogout: () => void;
  userEmail?: string;
  user?: User;
  fullWidth?: boolean;
  isNavigating?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  menuItems,
  selectedMenu,
  onMenuSelect,
  onLogout,
  userEmail,
  user,
  fullWidth = false,
  isNavigating = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebar = useSidebar();
  const {
    isOpen: sidebarOpen,
    toggle: toggleSidebar,
    isMobile,
    mobileOpen,
    closeMobile,
    currentWidth,
  } = sidebar;

  // Global PDF status management
  const pdfStatusManager = usePdfStatus();

  const selectedMenuLabel = menuItems.find(item => item.id === selectedMenu)?.label || 'Dashboard';

  // Boundary-role (External / Partner) users get a guaranteed "portal floor" — the items
  // explicitly gated to their role in menuItems.tsx — UNION whatever an admin has granted them
  // via /admin/menu-permissions (allowedMenuIds, the DB-driven path). The floor means an admin
  // can never lock a portal-confined role out of its own portal; the union means the DB matrix
  // still governs everything beyond it. What boundary users must NOT get is the normal filter's
  // escape hatch (items with no `roles` are universally visible), which would leak internal
  // groups (Dashboard, People, Norms, Configuration…) into their sidebar — the server-side
  // boundary still blocks the data, but the sidebar shouldn't promise screens that bounce.
  // Applies when the account holds ONLY boundary roles. Mirrors AuthContext.canAccessMenu.
  // PartnerProductManager is a Partner-portal add-on capability, not a separate persona — an account
  // holding it alongside Partner is still portal-confined, and the per-item `roles` (which gate the
  // single `partner-products` entry) keep it from leaking to a plain Partner. Including it here keeps
  // a (Partner + PartnerProductManager) user on the boundary-only path so the partner portal floor
  // still applies; the role-keyed filter below shows Products to managers only. It is also used by the
  // standard-path persona-hide check so an internal/admin sidebar never surfaces partner-products.
  // Sourced from the shared BOUNDARY_ROLES constant so AppLayout's portal confinement and this
  // sidebar filter can never disagree about which roles are portal-confined.
  const isBoundaryOnly =
    (user?.roles?.length ?? 0) > 0 &&
    (user?.roles?.every(r => BOUNDARY_ROLES.includes(r)) ?? false);

  // External-portal IA switch (plan P15): the new Home·Samples·Reports·Farms·Profile nav, the
  // "Submit a sample" action and the analyses chip exist ONLY under the new IA — one predicate
  // (useBillingIa: boundary-only + External + billingEnabled) shared with routes/hooks/Home.
  const billingIa = useBillingIa();
  const showFab =
    billingIa &&
    isMobile &&
    !FAB_HIDDEN_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));

  const handleSubmitSample = useCallback(() => {
    navigate(SUBMIT_SAMPLE_PATH);
  }, [navigate]);

  // Filter menu items based on database-driven allowed menu IDs (with fallback to role-based)
  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    if (isBoundaryOnly) {
      return items
        .filter(item => {
          const visible =
            (item.roles?.some(r => user?.roles?.includes(r)) ?? false) ||
            (user?.allowedMenuIds?.includes(item.id) ?? false);
          if (!visible) return false;
          // Free-tier gate: hide the batches/reports portal ("My Reports") until the External
          // account has row-level data access. Mirrors AuthContext.canAccessMenu — only an explicit
          // hasDataAccess === false hides it, so existing granted agents keep the entry.
          if (item.id === 'agent-portal' && user?.hasDataAccess === false) return false;
          return true;
        })
        .map(item =>
          item.children && item.children.length > 0
            ? { ...item, children: filterMenuItems(item.children) }
            : item,
        )
        .map(item => {
          // Resolve the IA switch into `hidden` so Sidebar + BottomNavigation (which both key off
          // `hidden`) agree: new-IA items appear only under billingIa; the legacy items it replaces
          // (and the staff-shaped `profile` entry — `/profile` redirects to Profile) disappear then.
          if (billingIa) {
            if (item.hiddenWhenBilling || item.id === 'profile') return { ...item, hidden: true };
            // SS External self-service (Phase 4a): mirror `requiresBilling` — a requiresSmartSampling
            // item shows only when the global `smartSamplingExternalEnabled` flag is on.
            if (item.requiresSmartSampling && user?.smartSamplingExternalEnabled !== true)
              return { ...item, hidden: true };
            return item;
          }
          return item.requiresBilling ? { ...item, hidden: true } : item;
        });
    }

    const allowedIds = user?.allowedMenuIds;
    const hasDbPermissions = allowedIds && allowedIds.length > 0;

    return items
      .filter(item => {
        // Portal items (partner-*/agent-portal) are persona-specific: hide them from anyone NOT
        // holding that boundary role — including admins, whose allowedMenuIds contain every id in
        // role_menu_permissions. Without this, the seeded portal rows surface dead entries in the
        // admin sidebar (clicking yields NO_PARTNER_ORG unless that account is partner-associated).
        // Admins can still reach the routes by URL; canAccessMenu is not narrowed here.
        if (
          item.roles?.some(r => BOUNDARY_ROLES.includes(r)) &&
          !item.roles.some(r => user?.roles?.includes(r))
        ) {
          return false;
        }
        // The new External IA exists only for boundary-only accounts (useBillingIa); a dual-role
        // External + staff account is on the internal surface and must never see its entries.
        if (item.requiresBilling) return false;
        // Same for the SS External self-service surface — never on the staff sidebar.
        if (item.requiresSmartSampling) return false;

        // Items with children are filtered by their children's visibility
        if (item.children && item.children.length > 0) {
          return true; // Will be filtered in the .map step below
        }

        // If database permissions are loaded, use them
        if (hasDbPermissions) {
          if (allowedIds.includes(item.id)) return true;
          // Items without a roles restriction that aren't in the DB are
          // universally accessible by design — show them even if they
          // haven't been seeded into role_menu_permissions yet.
          if (!item.roles || item.roles.length === 0) return true;
          return false;
        }

        // Fallback: use hardcoded roles (backward compatibility before first admin config)
        if (!item.roles || item.roles.length === 0) {
          return true;
        }
        return user?.roles?.some(userRole => item.roles?.includes(userRole)) ?? false;
      })
      .map(item => {
        if (item.children && item.children.length > 0) {
          const filteredChildren = filterMenuItems(item.children);
          if (filteredChildren.length === 0 && !item.path) {
            return null;
          }
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item): item is MenuItem => item !== null);
  };

  const filteredMenuItems = filterMenuItems(menuItems);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        selectedMenu={selectedMenu}
        onMenuSelect={onMenuSelect}
        menuItems={filteredMenuItems}
        userEmail={userEmail}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={closeMobile}
        currentWidth={currentWidth}
        onToggle={toggleSidebar}
        onSubmitSample={billingIa && !isMobile ? handleSubmitSample : undefined}
      />

      {/* Main Content Area */}
      <Box 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          // A flex item's automatic min-size is its content's min-content width, so without this a wide
          // table inside a page widens the whole column (AppBar included) and the BODY scrolls sideways on
          // phones. Clamped here, wide content scrolls inside its own `overflowX: auto` container instead.
          minWidth: 0,
          // Remove margin - let the sidebar handle positioning
          marginLeft: 0,
          // Add bottom padding for mobile bottom nav (+ room for the Submit FAB when it renders)
          paddingBottom: isMobile ? (showFab ? '136px' : '64px') : 0,
        }}
      >
        {/* Top App Bar */}
        <AppBar
          onToggleSidebar={toggleSidebar}
          onLogout={onLogout}
          selectedMenuLabel={selectedMenuLabel}
          isMobile={isMobile}
          showAnalysesChip={billingIa}
        />

        {/* Navigation loading indicator */}
        {isNavigating && (
          <LinearProgress
            sx={{
              height: 3,
              flexShrink: 0,
            }}
          />
        )}

        {/* Page Content */}
        <Box sx={{ 
          flex: 1, 
          p: { xs: 1, sm: 2, md: 3 },
          bgcolor: 'background.default',
          overflow: 'auto',
          position: 'relative',
          minHeight: 0, // Important for proper flex behavior
          minWidth: 0, // Same reason as the column above: never let content widen the page
        }}>
          {/* Background Pattern - Less opaque for better readability */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.01,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23487F3E' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              zIndex: 0
            }}
          />
          {fullWidth ? (
            <Box sx={{ position: 'relative', zIndex: 1, height: '100%' }}>
              {children}
            </Box>
          ) : (
            <Container 
              maxWidth="xl" 
              sx={{ 
                position: 'relative', 
                zIndex: 1,
                height: '100%',
                // Reduce horizontal padding on smaller screens
                px: { xs: 1, sm: 2, md: 3 }
              }}
            >
              {children}
            </Container>
          )}
        </Box>
      </Box>

      {/* Mobile "Submit a sample" — the sidebar CTA becomes a FAB above the bottom nav (design §4 shell). */}
      {showFab && (
        <Fab
          color="primary"
          aria-label="Submit a sample"
          onClick={handleSubmitSample}
          sx={{ position: 'fixed', right: 16, bottom: 64 + 16, zIndex: 1100 }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <BottomNavigation
          menuItems={filteredMenuItems}
          selectedMenu={selectedMenu}
          onMenuSelect={onMenuSelect}
        />
      )}

      {/* Global PDF Status Notifications */}
      <PdfStatusNotification
        jobs={pdfStatusManager.jobs}
        onJobComplete={(_jobId, _downloadUrl) => {
          // Job completion is already handled by the status manager
        }}
        onJobRemove={pdfStatusManager.removeJob}
        onJobRetry={pdfStatusManager.retryJob}
      />
    </Box>
  );
};
